import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { PageHeader } from '@/components/common/PageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import { EmptyState } from '@/components/common/EmptyState'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Organization = Tables<'organizations'>
type Profile = Tables<'profiles'>
type Order = Tables<'orders'>

export function AdminOrganizationDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [org, setOrg] = useState<Organization | null>(null)
  const [users, setUsers] = useState<Profile[]>([])
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ name: '', slug: '', email: '', phone: '', website: '', is_active: true })

  useEffect(() => {
    if (!id) return
    async function load() {
      const [orgRes, usersRes, ordersRes] = await Promise.all([
        supabase.from('organizations').select('*').eq('id', id!).single(),
        supabase.from('profiles').select('*').eq('organization_id', id!).order('full_name'),
        supabase.from('orders').select('*').eq('organization_id', id!).order('created_at', { ascending: false }).limit(10),
      ])
      if (orgRes.data) {
        setOrg(orgRes.data)
        setForm({
          name: orgRes.data.name,
          slug: orgRes.data.slug,
          email: orgRes.data.email || '',
          phone: orgRes.data.phone || '',
          website: orgRes.data.website || '',
          is_active: orgRes.data.is_active,
        })
      }
      setUsers(usersRes.data || [])
      setOrders(ordersRes.data || [])
      setLoading(false)
    }
    load()
  }, [id])

  if (loading) return <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p>
  if (!org) return <EmptyState title="Organization not found" description="This organization does not exist" />

  const totalRevenue = orders.reduce((sum, o) => sum + Number(o.total), 0)

  const saveChanges = async () => {
    await supabase.from('organizations').update({
      name: form.name,
      slug: form.slug,
      email: form.email || null,
      phone: form.phone || null,
      website: form.website || null,
      is_active: form.is_active,
    }).eq('id', org.id)
    setOrg({ ...org, ...form, email: form.email || null, phone: form.phone || null, website: form.website || null })
    setEditing(false)
  }

  return (
    <div>
      <PageHeader title={org.name} description={`Slug: ${org.slug} · Created ${new Date(org.created_at).toLocaleDateString()}`} actions={
        <>
          <button className={editing ? css.btnPrimary : css.btnSecondary} onClick={() => {
            if (editing) saveChanges()
            else setEditing(true)
          }}>
            {editing ? 'Save Changes' : 'Edit'}
          </button>
          {editing && (
            <button className={css.btnSecondary} onClick={() => setEditing(false)}>Cancel</button>
          )}
        </>
      } />

      {/* Edit Form */}
      {editing && (
        <div className={css.card} style={{ marginBottom: 'var(--space-6)' }}>
          <div className={css.formRow} style={{ marginBottom: 'var(--space-4)' }}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Name</label>
              <input className={css.formInput} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Slug</label>
              <input className={css.formInput} value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} />
            </div>
          </div>
          <div className={css.formRow} style={{ marginBottom: 'var(--space-4)' }}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Email</label>
              <input type="email" className={css.formInput} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Phone</label>
              <input className={css.formInput} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
          <div className={css.formRow} style={{ marginBottom: 'var(--space-4)' }}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Website</label>
              <input className={css.formInput} value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Status</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer', paddingTop: 'var(--space-3)' }}>
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                <span style={{ fontSize: 'var(--text-sm)' }}>Active</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Metrics */}
      <div className={css.grid3} style={{ marginBottom: 'var(--space-6)' }}>
        <div className={css.widget}>
          <div className={css.widgetTitle}>Total Users</div>
          <div className={css.widgetValue}>{users.length}</div>
        </div>
        <div className={css.widget}>
          <div className={css.widgetTitle}>Total Orders</div>
          <div className={css.widgetValue}>{orders.length}</div>
        </div>
        <div className={css.widget}>
          <div className={css.widgetTitle}>Revenue</div>
          <div className={css.widgetValue}>£{totalRevenue.toLocaleString()}</div>
        </div>
      </div>

      {/* Users */}
      <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
        Users ({users.length})
      </h3>
      {users.length === 0 ? (
        <div className={css.card} style={{ marginBottom: 'var(--space-8)' }}>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>No users assigned to this organization</p>
        </div>
      ) : (
        <div className={css.card} style={{ padding: 0, overflow: 'hidden', marginBottom: 'var(--space-8)' }}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 500 }}>{u.full_name || '—'}</td>
                  <td style={{ fontSize: 'var(--text-sm)' }}>{u.email}</td>
                  <td><StatusBadge label={u.role} /></td>
                  <td>
                    <StatusBadge
                      label={u.is_active ? 'active' : 'inactive'}
                      variant={u.is_active ? 'success' : 'default'}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent Orders */}
      <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
        Recent Orders
      </h3>
      {orders.length === 0 ? (
        <div className={css.card}>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>No orders from this organization</p>
        </div>
      ) : (
        <div className={css.card} style={{ padding: 0, overflow: 'hidden' }}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Status</th>
                <th>Total</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} className={css.tableClickableRow} onClick={() => navigate(`/admin/orders/${o.id}`)}>
                  <td><code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{o.reference_code}</code></td>
                  <td><StatusBadge label={o.status} /></td>
                  <td style={{ fontWeight: 500 }}>£{Number(o.total).toLocaleString()}</td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>{new Date(o.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
